import React from 'react';
import icons from './icons';
import { iconPropTypes, iconDefaultProps } from './icon.propTypes';
import { useTheme } from '@mui/material';
import { StyledBackground } from './icon.styles';

/**
 * Icon component that renders a specified icon with customizable properties.
 *
 * This component utilizes MUI's theme and dynamically imports icons from the `icons` module.
 *
 * @component
 * @param {Object} props - The properties for the Icon component.
 * @param {string} props.type - The type of the icon to render. Must match a key from the `icons` module.
 * @param {string} [props.fontSize] - The size of the icon. Can be one of 'small', 'medium', 'large', or a custom string.
 * @param {string} [props.color] - The color of the icon. Can be one of 'primary', 'secondary', or a custom color string.
 * @param {string} [props.backgroundColor] - The background color of the icon. Can be one of 'primary', 'secondary', or a custom color string.
 * @param {string} [props.backgroundShape] - The shape of the icon's background. Can be 'square', 'square-rounded', or 'circle'.
 * @param {string|number} [props.backgroundWidth] - The width of the icon's background. Can be a string or number.
 * @param {string|number} [props.backgroundHeight] - The height of the icon's background. Can be a string or number.
 * @param {string|number} [props.borderSize] - The size of the border around the icon. Can be a string or number.
 * @param {string} [props.borderColor] - The color of the border around the icon. Can be one of 'primary', 'secondary', or a custom color string.
 * @param {boolean} [props.hasBackground] - Whether to render the background or not. If false, only the icon is rendered without background.
 * @returns {JSX.Element} The rendered icon component.
 */
export default function Icon({
  type,
  fontSize,
  color,
  backgroundColor,
  backgroundShape,
  backgroundWidth,
  backgroundHeight,
  borderSize,
  borderColor,
  hasBackground,
  sx,
}) {
  const theme = useTheme();
  const IconComponent = icons[type];
  if (!IconComponent) {
    return null;
  }
  if (hasBackground) {
    return (
      <StyledBackground
        backgroundcolor={backgroundColor}
        backgroundshape={backgroundShape}
        backgroundwidth={backgroundWidth}
        backgroundheight={backgroundHeight}
        bordersize={borderSize}
        bordercolor={borderColor}
        theme={theme}
      >
        <IconComponent
          fontSize={fontSize}
          color={color}
          title={`${type} icon`}
          sx={sx}
        />
      </StyledBackground>
    );
  }
  return (
    <IconComponent
      fontSize={fontSize}
      color={color}
      title={`${type} icon`}
      sx={sx}
    />
  );
}
Icon.propTypes = iconPropTypes;
Icon.defaultProps = iconDefaultProps;
